import React, { useEffect, useMemo, useState } from "react";
import { X, Loader2, Disc3, Download, AudioLines, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ShoutoutMontageModal({ onClose, onRendered, onShare }) {
  const [cands, setCands] = useState(null);
  const [beds, setBeds] = useState([]);
  const [sel, setSel] = useState({});
  const [bedId, setBedId] = useState("");
  const [title, setTitle] = useState("");
  const [days, setDays] = useState(7);
  const [includeIntro, setIncludeIntro] = useState(false);
  const [introText, setIntroText] = useState("");
  const [rendering, setRendering] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, b] = await Promise.all([
          api.get(`/request-line/montage/candidates?days=${days}`),
          api.get("/request-line/montage/beds"),
        ]);
        setCands(c.data.candidates);
        setSel(Object.fromEntries(c.data.candidates.map((x) => [x.request_id, true])));
        setBeds(b.data.beds);
      } catch { toast.error("Could not load shoutout candidates"); }
    })();
  }, [days]);

  const chosen = useMemo(() => (cands || []).filter((c) => sel[c.request_id]), [cands, sel]);

  const render = async () => {
    if (!chosen.length) { toast.error("Pick at least one shoutout"); return; }
    setRendering(true);
    try {
      const { data } = await api.post("/request-line/montage/render", {
        request_ids: chosen.map((c) => c.request_id),
        bed_asset_id: bedId,
        title,
        include_intro: includeIntro,
        intro_text: introText,
      });
      setResult(data);
      onRendered?.(data);
      toast.success("Montage rendered — it's in your Imaging library too");
    } catch (e) { toast.error(e?.response?.data?.detail || "Render failed"); }
    finally { setRendering(false); }
  };

  const download = async () => {
    try {
      const r = await api.get(`/imaging/assets/${result.asset_id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${result.title.replace(/[^\w-]+/g, "-")}.mp3`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="montage-modal">
      <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-xl border border-line bg-bg-surface p-6">
        <div className="flex items-center justify-between mb-1">
          <div className="inline-flex items-center gap-2 font-display font-bold text-lg">
            <Disc3 className="h-5 w-5 text-purple-300" /> Shoutout Roundup
          </div>
          <button onClick={onClose} data-testid="montage-close"
            className="w-7 h-7 rounded-md flex items-center justify-center border border-line text-ink-muted hover:text-ink-primary transition">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="text-xs text-ink-secondary mb-4">
          Stitches the selected listener voice shoutouts into one ready-to-air MP3 — with a music bed underneath if you pick one.
        </div>

        {result ? (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="montage-result">
            <div className="text-sm font-semibold mb-1">{result.title}</div>
            <div className="text-[11px] text-ink-muted mb-3">
              {result.clip_count} shoutout{result.clip_count === 1 ? "" : "s"} · {(result.duration_ms / 1000).toFixed(0)}s
              {result.bed_label ? ` · bed: ${result.bed_label}` : " · no bed"} · saved to your Imaging library
            </div>
            <audio controls className="w-full mb-3" src={`${BACKEND}${result.audio_url}`} data-testid="montage-result-audio" />
            <div className="flex gap-2">
              <button onClick={download} data-testid="montage-download-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black">
                <Download className="h-4 w-4" /> Download MP3
              </button>
              {onShare && (
                <button onClick={() => onShare(result)} data-testid="montage-share-result-btn"
                  className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/50 bg-brand-amber/10 px-4 py-2 text-sm font-semibold text-brand-amber hover:bg-brand-amber/20 transition">
                  <Share2 className="h-4 w-4" /> Post to socials
                </button>
              )}
              <button onClick={() => setResult(null)} data-testid="montage-again-btn"
                className="rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm font-semibold text-ink-secondary hover:text-brand-amber transition">
                Build another
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3">
              <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">From the last</label>
              <select value={days} onChange={(e) => setDays(+e.target.value)} data-testid="montage-days"
                className="rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs outline-none">
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>

            {cands === null ? (
              <div className="text-xs text-ink-muted py-6"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" />Looking for approved voice shoutouts...</div>
            ) : cands.length === 0 ? (
              <div className="text-sm text-ink-muted py-8 text-center border border-dashed border-line rounded-lg" data-testid="montage-empty">
                No approved voice shoutouts in this window — approve some listener recordings first.
              </div>
            ) : (
              <div className="flex flex-col gap-1.5 max-h-60 overflow-y-auto sidebar-scroll mb-4" data-testid="montage-candidates">
                {cands.map((c) => (
                  <label key={c.request_id} data-testid={`montage-cand-${c.request_id}`}
                    className={`flex items-start gap-2.5 rounded-lg border p-2.5 cursor-pointer transition ${
                      sel[c.request_id] ? "border-purple-500/50 bg-purple-500/5" : "border-line bg-bg-elevated opacity-60"
                    }`}>
                    <input type="checkbox" checked={!!sel[c.request_id]}
                      onChange={(e) => setSel((s) => ({ ...s, [c.request_id]: e.target.checked }))}
                      className="mt-0.5 accent-[#a78bfa]" />
                    <span className="min-w-0">
                      <span className="block text-[12.5px] font-semibold truncate">
                        {c.listener_name || "Anonymous"} — "{c.song_title}"
                      </span>
                      <span className="block text-[11px] text-ink-secondary italic truncate">
                        {c.voice_shoutout?.transcript || "No transcript — listen in the queue"}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Music bed (optional)</label>
                <select value={bedId} onChange={(e) => setBedId(e.target.value)} data-testid="montage-bed"
                  className="w-full rounded-md border border-line bg-bg-elevated px-2 py-2 text-xs outline-none">
                  <option value="">No bed — voices only</option>
                  {beds.map((b) => (
                    <option key={b.asset_id} value={b.asset_id}>{b.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Title</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="montage-title"
                  placeholder={`Shoutout Roundup — ${new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}
                  className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs outline-none focus:border-brand-amber/60" />
              </div>
            </div>

            <div className="rounded-lg border border-line bg-bg-elevated p-3 mb-4" data-testid="montage-intro-panel">
              <label className="flex items-center gap-2.5 text-[12.5px] cursor-pointer">
                <input type="checkbox" checked={includeIntro} data-testid="montage-intro-toggle"
                  onChange={(e) => setIncludeIntro(e.target.checked)} className="accent-[#a78bfa]" />
                <span className="font-semibold">Open with an AI-voiced intro</span>
                <span className="text-[10.5px] text-ink-muted">uses your Request Line shoutout voice</span>
              </label>
              {includeIntro && (
                <input value={introText} onChange={(e) => setIntroText(e.target.value)} maxLength={200}
                  data-testid="montage-intro-text"
                  placeholder="These are this week's listener shoutouts on your station!"
                  className="mt-2 w-full rounded-md border border-line bg-bg-surface px-3 py-2 text-xs outline-none focus:border-brand-amber/60" />
              )}
            </div>

            <button onClick={render} disabled={rendering || !chosen.length} data-testid="montage-render-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-brand-amber py-3 text-sm font-bold text-black amber-glow disabled:opacity-50">
              {rendering ? <Loader2 className="h-4 w-4 animate-spin" /> : <AudioLines className="h-4 w-4" />}
              {rendering ? "Mixing your roundup…" : `Render montage (${chosen.length} shoutout${chosen.length === 1 ? "" : "s"})`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
